import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Reviews from "@/components/Reviews";
import useReveal from "@/hooks/useReveal";
import {
  fetchNextEditionNumber,
  fetchProduct,
  fetchReviews,
  fetchStockStatus,
} from "@/lib/api";
import { getShopifyUrl } from "@/lib/shopify";

export default function ProductPage() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [stock, setStock] = useState(null);
  const [nextEdition, setNextEdition] = useState(null);
  const [reviewSummary, setReviewSummary] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [loaded, setLoaded] = useState(false);

  const r1 = useReveal();
  const r2 = useReveal();

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [p, s] = await Promise.all([
          fetchProduct(slug),
          fetchStockStatus(slug),
        ]);
        if (mounted) {
          setProduct(p);
          setStock(s);
          setLoaded(true);
        }
        // Edition number is only meaningful for the active sunglasses preorder.
        if (mounted && p && p.status !== "coming_soon") {
          try {
            const ne = await fetchNextEditionNumber(slug);
            if (mounted) setNextEdition(ne);
          } catch {
            /* non-blocking */
          }
        }
        // Lightweight review summary for the header (stars + count link).
        try {
          const rs = await fetchReviews(slug, { page: 1, perPage: 1 });
          if (mounted && rs && rs.product_id) setReviewSummary(rs);
        } catch {
          /* non-blocking */
        }
      } catch (err) {
        if (mounted) {
          setLoaded(true);
          toast.error("Product not found");
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, [slug]);

  if (!loaded) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <span className="font-label text-[#d4af37]">Loading…</span>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white">
        <Header />
        <div className="pt-40 px-6 text-center">
          <h1 className="font-display text-3xl">Product not found.</h1>
          <Link to="/" className="mt-8 inline-block btn-ghost-gold">
            Return Home
          </Link>
        </div>
      </div>
    );
  }

  const isComingSoon = product.status === "coming_soon";
  const shopifyUrl = getShopifyUrl(product.slug);

  return (
    <div data-testid="product-page" className="bg-[#0a0a0a] text-white">
      <Header />
      <main className="pt-24 md:pt-32 pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          {/* Breadcrumb */}
          <div className="font-label text-[#8a8a85] mb-10">
            <button
              type="button"
              onClick={() => navigate("/")}
              className="link-underline"
              data-testid="breadcrumb-home"
            >
              Home
            </button>
            <span className="mx-3">/</span>
            <span className="text-[#d4af37]">{product.name}</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            {/* Gallery */}
            <div
              ref={r1.ref}
              className={`reveal ${r1.inView ? "in-view" : ""} lg:col-span-7`}
            >
              <div className="zoom-wrap aspect-[4/5] bg-[#0c0c0c] border border-[#1f1f1f] relative">
                <img
                  src={product.images[activeImg] || product.images[0]}
                  alt={product.name}
                  className="w-full h-full object-cover"
                  data-testid="product-active-image"
                />
                <span
                  aria-hidden
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.40) 100%)",
                  }}
                />
              </div>
              {product.images.length > 1 && (
                <div className="mt-4 grid grid-cols-4 gap-3">
                  {product.images.map((src, i) => (
                    <button
                      key={src}
                      type="button"
                      onClick={() => setActiveImg(i)}
                      data-testid={`product-thumb-${i}`}
                      className={`aspect-square overflow-hidden border ${
                        activeImg === i
                          ? "border-[#d4af37]"
                          : "border-[#1f1f1f] hover:border-[#3a3a3a]"
                      } transition-colors`}
                    >
                      <img
                        src={src}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Info */}
            <div
              ref={r2.ref}
              className={`reveal reveal-delay-1 ${
                r2.inView ? "in-view" : ""
              } lg:col-span-5`}
            >
              <span
                className="inline-flex items-center gap-2 border border-[#d4af37]/70 text-[#d4af37] font-label py-1.5 px-3"
                data-testid="product-status-badge"
              >
                <span className="pulse-dot" />
                {isComingSoon ? "Coming Soon" : "Only 300 Pieces Available"}
              </span>

              <h1 className="mt-6 font-display text-[clamp(2rem,4.6vw,3.8rem)] leading-[1.05] tracking-tight">
                {product.name.replace("™", "")}
                <span className="gold text-[0.35em] align-super ml-1">™</span>
              </h1>
              <p className="mt-3 font-editorial italic text-[#d4d4cf] text-xl">
                {product.tagline}
              </p>

              {reviewSummary && reviewSummary.number_of_reviews > 0 && (
                <a
                  href="#product-reviews-anchor"
                  data-testid="product-reviews-summary"
                  className="mt-4 inline-flex items-center gap-3 group"
                  onClick={(e) => {
                    e.preventDefault();
                    document
                      .getElementById("product-reviews-anchor")
                      ?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                >
                  <span
                    className="inline-flex tracking-[0.15em] text-[#d4af37]"
                    aria-label={`${reviewSummary.average_rating} out of 5 stars`}
                  >
                    {[1, 2, 3, 4, 5].map((n) => (
                      <span
                        key={n}
                        className={
                          n <= Math.round(reviewSummary.average_rating)
                            ? "text-[#d4af37]"
                            : "text-[#3a3a3a]"
                        }
                      >
                        ★
                      </span>
                    ))}
                  </span>
                  <span className="font-label text-[#a3a3a3] group-hover:text-[#d4af37] transition-colors">
                    {reviewSummary.average_rating.toFixed(1)} ·{" "}
                    {reviewSummary.number_of_reviews}{" "}
                    {reviewSummary.number_of_reviews === 1
                      ? "review"
                      : "reviews"}
                  </span>
                </a>
              )}

              <p className="mt-8 text-[#bdbdb7] leading-relaxed font-light">
                {product.description}
              </p>

              <div className="mt-10 flex items-end gap-10">
                <div>
                  <span className="font-label text-[#8a8a85]">Price</span>
                  <p
                    className="mt-2 font-display text-white text-4xl"
                    data-testid="product-price"
                  >
                    ${product.price.toFixed(0)}{" "}
                    <span className="text-[#8a8a85] text-base">
                      {product.currency}
                    </span>
                  </p>
                </div>
                {stock && (
                  <div>
                    <span className="font-label text-[#8a8a85]">Remaining</span>
                    <p
                      className="mt-2 font-display text-white text-3xl"
                      data-testid="product-remaining"
                    >
                      {stock.remaining}
                      <span className="text-[#8a8a85] text-base">
                        {" "}
                        / {stock.total_pieces}
                      </span>
                    </p>
                  </div>
                )}
              </div>

              {!isComingSoon && nextEdition && (
                <div
                  className="mt-10 relative border border-[#d4af37]/30 bg-[#0c0c0c] p-5"
                  data-testid="next-edition-teaser"
                >
                  <span
                    aria-hidden
                    className="absolute -top-px -left-px w-4 h-4 border-l border-t border-[#d4af37]"
                  />
                  <span
                    aria-hidden
                    className="absolute -top-px -right-px w-4 h-4 border-r border-t border-[#d4af37]"
                  />
                  <span
                    aria-hidden
                    className="absolute -bottom-px -left-px w-4 h-4 border-l border-b border-[#d4af37]"
                  />
                  <span
                    aria-hidden
                    className="absolute -bottom-px -right-px w-4 h-4 border-r border-b border-[#d4af37]"
                  />
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <span className="font-label text-[#d4af37]">
                        Reserve Your Edition Number
                      </span>
                      <p className="mt-2 text-[#a3a3a3] text-sm font-light max-w-xs leading-relaxed">
                        Your piece arrives numbered & signed. The next
                        available number is yours at checkout.
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="font-label text-[#8a8a85]">Next</span>
                      <p
                        className="font-editorial italic gold-text-gradient text-4xl leading-none mt-1"
                        data-testid="next-edition-number"
                      >
                        #{String(nextEdition.next_number).padStart(3, "0")}
                      </p>
                      <span className="font-label text-[#6a6a65] text-[10px]">
                        of {String(nextEdition.total_pieces).padStart(3, "0")}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {isComingSoon ? (
                <button
                  type="button"
                  disabled
                  data-testid="product-preorder-btn"
                  className="btn-gold w-full mt-10 opacity-60 cursor-not-allowed"
                >
                  Coming Soon
                </button>
              ) : shopifyUrl ? (
                <a
                  href={shopifyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="product-preorder-btn"
                  className="btn-gold w-full mt-10 justify-center"
                >
                  Preorder on Shopify
                  <span aria-hidden>→</span>
                </a>
              ) : (
                <button
                  type="button"
                  disabled
                  data-testid="product-preorder-btn"
                  className="btn-gold w-full mt-10 opacity-60 cursor-not-allowed"
                >
                  Coming Soon
                </button>
              )}

              <p className="mt-5 font-label text-[#6a6a65] text-center">
                Secure checkout · Shopify
              </p>

              {/* Specs */}
              <div className="mt-14 border-t border-[#1f1f1f]">
                {product.specs.map((s, i) => (
                  <div
                    key={s.label}
                    className="flex items-center justify-between py-4 border-b border-[#1f1f1f]"
                    data-testid={`product-detail-spec-${i}`}
                  >
                    <span className="font-label text-[#8a8a85]">
                      {s.label}
                    </span>
                    <span className="text-white text-right">{s.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Reviews */}
          <div id="product-reviews-anchor" />
          <Reviews slug={product.slug} />
        </div>
      </main>
      <Footer />
    </div>
  );
}
